import { useState, useCallback } from "react";
import { supabase } from "@/shared/lib/supabase";
import { EMPTY_ADDRESS, type AddressParts } from "@/shared/utils/addressUtils";

export interface ContactFormData {
  fullName: string;
  firstName: string;
  middleName: string;
  lastName: string;
  legalRole: Array<{ value: string; label: string }>;
  relationship: string;
  email: string;
  phone: string;
  address: AddressParts;
  inviteToElayne: boolean;
}

export interface ContactFormErrors {
  fullName?: string;
  firstName?: string;
  legalRole?: string;
  email?: string;
  phone?: string;
}

export const useContactForm = (estateId?: number) => {
  const [formData, setFormData] = useState<ContactFormData>({
    fullName: "",
    firstName: "",
    middleName: "",
    lastName: "",
    legalRole: [],
    relationship: "",
    email: "",
    phone: "",
    address: { ...EMPTY_ADDRESS },
    inviteToElayne: true,
  });

  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const validateForm = useCallback(() => {
    const newErrors: ContactFormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (formData.phone) {
      const digits = formData.phone.replace(/\D/g, "");
      if (digits.length > 0 && digits.length < 10) {
        newErrors.phone = "Please enter a valid 10-digit phone number";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleInputChange = useCallback(
    (
      field: keyof Omit<ContactFormData, "address">,
      value: string | boolean | Array<{ value: string; label: string }>,
    ) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      if (errors[field as keyof ContactFormErrors]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    },
    [errors],
  );

  const handleAddressChange = useCallback((value: AddressParts) => {
    setFormData((prev) => ({
      ...prev,
      address: value,
    }));
  }, []);

  const handleProfilePictureSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setProfilePictureUrl(url);
    }
  }, []);

  const uploadProfilePicture = useCallback(async (file: File): Promise<string | null> => {
    try {
      // Get access token for authorization
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error("No access token available");
      }

      // Step 1: Get presigned URL
      const presignedResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/media/profile-picture/presigned-url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            fileName: file.name,
            estateId,
          }),
        },
      );

      if (!presignedResponse.ok) {
        throw new Error("Failed to get presigned URL");
      }

      const { uploadUrl, fileKey } = await presignedResponse.json();

      // Step 2: Upload file to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file to S3");
      }

      return fileKey;
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      throw error;
    }
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      fullName: "",
      firstName: "",
      middleName: "",
      lastName: "",
      legalRole: [],
      relationship: "",
      email: "",
      phone: "",
      address: { ...EMPTY_ADDRESS },
      inviteToElayne: true,
    });
    setErrors({});
    setIsSubmitting(false);
    setProfilePictureUrl(null);
    setSelectedFile(null);
  }, []);

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    isSubmitting,
    setIsSubmitting,
    profilePictureUrl,
    setProfilePictureUrl,
    selectedFile,
    validateForm,
    handleInputChange,
    handleAddressChange,
    handleProfilePictureSelect,
    uploadProfilePicture,
    resetForm,
  };
};
